// ./src/components/features/EventFeatures/EventFeatureChecklist.tsx
//
// "What will this event use?" — the list of EVENT features a host turns on (ROADMAP A2b #62 / #63). Used by the
// wizard's Features step now, and by the "Event features" settings page (P5) — one component, so the two can never
// disagree about what is offered, suggested, unavailable or premium.
//
// ⭐ DISCOVERY WITHOUT OVERWHELM (the item's stated goal): what THIS type of event usually uses comes first
// ("Suggested for a Christmas party"); everything else sits under "More features", closed until asked for.
//
// What decides each card — none of it re-implemented here:
//   - ON / suggested / UNAVAILABLE come from the seeded rules, run by the GENERATED evaluator. A card the mode cannot
//     have says why, and offers the mode switch that would allow it as the host's own choice ("Sell tickets").
//   - A feature a rule turns on (a ticketed event has Tickets) is shown on and locked, saying why.
//   - PREMIUM: a feature the account's plan does not include carries the premium icon AND the names of the plans that
//     do, as visible text (a tooltip is not an affordance on touch). It can still be ticked — the page itself shows
//     the upgrade screen when opened; the wizard never sends anyone to billing mid-setup.
// ⚠ Turning a feature off NEVER deletes anything — it only tidies the menu, and the menu still shows a page that has
//   data (#63). The copy says so, because "off" next to a budget with rows reads as "delete".

import React, { useMemo, useState } from 'react';
import { Stack, Group, Text, SimpleGrid, Box, Button, Badge, Collapse, UnstyledButton } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { useAppEnvironment, useOptionalEventSetupCatalogContext } from '@/contexts';
import { AppIcon } from '@/components/generic';
import { useFeatureGate } from '@/hooks';
import type { EventSetupType } from '@/utils/api';
import { EVENT_FEATURES, EventFeatureDef, evaluateSetupRules } from '@/config/eventSetup.generated';
import { PlanLike, joinPlanNames, plansIncludingFeature } from '@/utils/eventFeaturePlans';
import type { FeatureSource } from '@/utils/analytics';
import { GAME_PRESETS } from '@/pages/private/EventGamesPage/presets';

import { WizardSelectCard } from '../CreateEventWizard/components/WizardSelectCard';

/** Menu-section headings, in the order the "More features" list shows them. */
const SECTION_LABELS: Array<{ section: string; label: string }> = [
  { section: 'overview', label: 'Overview' },
  { section: 'registration', label: 'Registration' },
  { section: 'program', label: 'Program' },
  { section: 'games', label: 'Games' },
  { section: 'operations', label: 'Operations' },
  { section: 'volunteers', label: 'Volunteers & staff' },
  { section: 'partners', label: 'Partners' },
  { section: 'inventory', label: 'Inventory' },
  { section: 'policies', label: 'Policies & compliance' },
  { section: 'website', label: 'Website' },
];

export interface EventFeatureChecklistProps {
  /** The event's type (drives the "Suggested for …" list); null → no suggestions, everything under one list. */
  type: EventSetupType | null;
  eventMode: string | null;
  /** The features the host has on. */
  value: string[];
  onChange: (next: string[]) => void;
  /** Offered when a card is unavailable in this mode and a switch would allow it. Omit to hide the switch. */
  onSwitchMode?: (eventMode: string) => void;
  /** The account's plans (appPlanList) — for "Available on Pro or Business". */
  plans: readonly PlanLike[];
  /** Disables every card while a save is in flight. */
  disabled?: boolean;
  /**
   * Features that get their own setup step after this one (the wizard's Vouchers / Tickets / Questions steps). A card
   * that is ON says so, so ticking it reads as "and I'll set it up next" rather than a switch that does nothing yet.
   */
  setupNext?: readonly string[];
  /**
   * #62 P7 — told of each switch the HOST makes (not forced or blocked cards), with where the card sat: the type's
   * suggestions, the type's other offers, or "More features". For the wizard's funnel.
   */
  onToggle?: (code: string, on: boolean, source: FeatureSource) => void;
}

const MODE_SWITCH_LABELS: Record<string, string> = {
  ticketed: 'Sell tickets instead',
  link_rsvp: 'Take RSVPs instead',
  managed_invites: 'Use an invite list instead',
};

export const EventFeatureChecklist: React.FC<EventFeatureChecklistProps> = ({
  type,
  eventMode,
  value,
  onChange,
  onSwitchMode,
  plans,
  disabled = false,
  setupNext = [],
  onToggle,
}) => {
  const { t } = useTranslation(['events', 'common']);
  const { isMobile } = useAppEnvironment();
  const catalog = useOptionalEventSetupCatalogContext();
  const { hasFeature } = useFeatureGate();
  const [moreOpen, setMoreOpen] = useState(false);

  const parent = type ? (catalog?.parentOf(type) ?? null) : null;
  // ⓘ Memoised: a fresh `[]` on every render would change what the memos below depend on (#129).
  const rules = useMemo(() => catalog?.rules ?? [], [catalog?.rules]);

  // ⚠ The generated evaluator decides — never a local re-reading of the rules.
  const outcome = useMemo(
    () =>
      evaluateSetupRules(rules, {
        eventMode,
        categoryCode: type?.code ?? null,
        parentCategoryCode: parent?.code ?? null,
        categoryGroup: parent?.categoryGroup ?? type?.categoryGroup ?? null,
        featuresOn: value,
        spaceId: null,
      }),
    [rules, eventMode, type, parent, value],
  );
  // What the rules turn on regardless of the host (a ticketed event has Tickets) — shown on and locked.
  const forced = useMemo(
    () =>
      evaluateSetupRules(rules, {
        eventMode,
        categoryCode: type?.code ?? null,
        parentCategoryCode: parent?.code ?? null,
        categoryGroup: parent?.categoryGroup ?? type?.categoryGroup ?? null,
        featuresOn: [],
        spaceId: null,
      }).on,
    [rules, eventMode, type, parent],
  );

  const offers = type?.offers ?? [];
  const offered = new Set(offers.map((o) => o.featureCode));
  const suggestedByType = new Set(offers.filter((o) => o.strength === 'suggested').map((o) => o.featureCode));

  // ⚠ The top list is what the TYPE offers AND what the RULES suggest — a suggestion hidden under a closed "More
  // features" is not a suggestion (a ticketed event suggests Finance, and choosing Finance suggests Budget: owner,
  // 2026-09-29, "they work together").
  const topCodes = [...offers.map((o) => o.featureCode), ...outcome.suggested.filter((code) => !offered.has(code))];
  const topSet = new Set(topCodes);
  const offeredDefs = topCodes
    .map((code) => EVENT_FEATURES.find((f) => f.code === code))
    .filter((f): f is EventFeatureDef => !!f);
  const moreDefs = EVENT_FEATURES.filter((f) => !topSet.has(f.code));
  // Where a card sits — the same tests that draw its "Suggested" badge and choose its list.
  const featureSourceOf = (code: string): FeatureSource => {
    if (suggestedByType.has(code) || outcome.suggested.includes(code)) {
      return 'suggested';
    }

    return topSet.has(code) ? 'offered' : 'more';
  };

  const toggle = (code: string) => {
    if (disabled || forced.includes(code) || outcome.disabled[code]) {
      return;
    }

    const turningOn = !value.includes(code);

    onChange(turningOn ? [...value, code] : value.filter((c) => c !== code));
    onToggle?.(code, turningOn, featureSourceOf(code));
  };

  const renderCard = (def: EventFeatureDef) => {
    const blocked = outcome.disabled[def.code];
    const isForced = forced.includes(def.code);
    const on = !blocked && (isForced || outcome.on.includes(def.code));
    const premium = !!def.planFeatureCode && !hasFeature(def.planFeatureCode);
    const planNames = premium ? plansIncludingFeature(plans, def.planFeatureCode) : [];
    const offer = offers.find((o) => o.featureCode === def.code);
    // The type's concrete suggestion — "Secret Santa" for a Christmas party, "Free first drink" for networking.
    const gameIdeas = (offer?.options?.gamePresets ?? [])
      .map((id) => GAME_PRESETS.find((p) => p.id === id)?.label)
      .filter((label): label is string => !!label);
    // …and the type's own step ideas (#62 P6(h)) — "Cake cutting, First dance" on a wedding's Timeline card.
    const stepIdeas = (offer?.options?.ideas ?? []).slice(0, 2);
    const idea =
      offer?.options?.voucherIdeas?.[0] ?? (gameIdeas.length ? gameIdeas.slice(0, 2).join(', ') : stepIdeas.length ? stepIdeas.join(', ') : null);

    const extra = (
      <Stack gap={4} mt={4}>
        {suggestedByType.has(def.code) || outcome.suggested.includes(def.code) ? (
          <Group gap={6}>
            <Badge size="md" variant="light" color="teal">
              {t('events:features.suggested', { defaultValue: 'Suggested' })}
            </Badge>
          </Group>
        ) : null}
        {idea ? (
          <Text size="md" c="dimmed">
            {t('events:features.idea', { defaultValue: 'Idea: {{idea}}', idea })}
          </Text>
        ) : null}
        {on && setupNext.includes(def.code) ? (
          <Text size="md" c="teal">
            {t('events:features.setupNext', { defaultValue: 'You’ll set this up in the next steps.' })}
          </Text>
        ) : null}
        {isForced ? (
          <Text size="md" c="dimmed">
            {t('events:features.forced', { defaultValue: 'Always on for this kind of event.' })}
          </Text>
        ) : null}
        {blocked ? (
          <Stack gap={4}>
            <Text size="md" c="dimmed">
              {blocked.reason}
            </Text>
            {blocked.unlock?.eventMode && onSwitchMode ? (
              <Box>
                <Button
                  size="md"
                  variant="light"
                  disabled={disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSwitchMode(blocked.unlock!.eventMode);
                  }}
                >
                  {t(`events:features.switch.${blocked.unlock.eventMode}`, {
                    defaultValue: MODE_SWITCH_LABELS[blocked.unlock.eventMode] ?? 'Switch the guest mode',
                  })}
                </Button>
              </Box>
            ) : null}
          </Stack>
        ) : null}
        {premium ? (
          <Group gap={6} align="flex-start" wrap="nowrap">
            <Box pt={3}>
              <AppIcon iconName="Crown" size={16} color="var(--mantine-color-yellow-6)" />
            </Box>
            <Text size="md" c="dimmed">
              {planNames.length
                ? t('events:features.premiumOn', {
                    defaultValue: 'Premium — included in {{plans}}. You can switch it on now and upgrade when you are ready.',
                    plans: joinPlanNames(planNames),
                  })
                : t('events:features.premium', { defaultValue: 'Premium — needs a plan upgrade to use.' })}
            </Text>
          </Group>
        ) : null}
      </Stack>
    );

    return (
      <WizardSelectCard
        key={def.code}
        title={def.name}
        description={def.description}
        icon={def.icon}
        selected={on}
        disabled={disabled || !!blocked || isForced}
        onClick={() => toggle(def.code)}
        extra={extra}
      />
    );
  };

  const cols = isMobile ? 1 : 2;
  const typeName = type?.name ?? null;

  return (
    <Stack gap="lg">
      {offeredDefs.length > 0 ? (
        <Stack gap="sm">
          <Text fw={600} size="md">
            {typeName
              ? t('events:features.forType', { defaultValue: 'Suggested for {{type}}', type: typeName })
              : t('events:features.forThis', { defaultValue: 'Suggested for this event' })}
          </Text>
          <SimpleGrid cols={cols} spacing="sm">
            {offeredDefs.map(renderCard)}
          </SimpleGrid>
        </Stack>
      ) : null}

      <Stack gap="sm">
        <UnstyledButton onClick={() => setMoreOpen((o) => !o)} aria-expanded={moreOpen}>
          <Group gap="xs">
            <AppIcon iconName={moreOpen ? 'ChevronDown' : 'ChevronRight'} size={18} />
            <Text fw={600} size="md">
              {t('events:features.more', { defaultValue: 'More features ({{count}})', count: moreDefs.length })}
            </Text>
          </Group>
        </UnstyledButton>
        <Collapse in={moreOpen || offeredDefs.length === 0}>
          <Stack gap="lg">
            {SECTION_LABELS.map(({ section, label }) => {
              const defs = moreDefs.filter((f) => f.section === section);

              if (defs.length === 0) {
                return null;
              }

              return (
                <Stack key={section} gap="sm">
                  <Text fw={600} size="md" c="dimmed">
                    {t(`events:features.section.${section}`, { defaultValue: label })}
                  </Text>
                  <SimpleGrid cols={cols} spacing="sm">
                    {defs.map(renderCard)}
                  </SimpleGrid>
                </Stack>
              );
            })}
          </Stack>
        </Collapse>
      </Stack>

      <Text size="md" c="dimmed">
        {t('events:features.offNote', {
          defaultValue:
            'Switching a feature off only tidies your menu — nothing is deleted, and a page that already has something in it stays.',
        })}
      </Text>
    </Stack>
  );
};
